import React, { useState } from "react";
import { ShieldCheck, AlertCircle, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ConfidenceBadge from "@/components/ConfidenceBadge";

/**
 * ComplianceShield — wraps any AI-generated output with the mandatory
 * disclaimer, confidence indicator, and one-tap physician attestation.
 *
 * Props:
 *   outputId:     unique id for this AI output (for the audit log)
 *   module:       module key (charting | prior-auth | eod | wellbeing | dictation | ...)
 *   confidence:   "high" | "moderate" | "low"
 *   summary:      brief human-readable summary of the output (≤500 chars, no PHI)
 *   sources:      optional array of {name, year, url?}
 *   onAttested:   optional callback after successful attestation
 */
export default function ComplianceShield({
  outputId,
  module,
  confidence = "moderate",
  summary = "",
  sources = [],
  onAttested,
}) {
  const [attesting, setAttesting] = useState(false);
  const [attested, setAttested] = useState(false);

  const attest = async (accepted) => {
    setAttesting(true);
    try {
      await api.post("/compliance/attest", {
        output_id: outputId,
        module,
        output_summary: summary,
        confidence,
        accepted,
      });
      setAttested(true);
      toast.success(accepted ? "Output attested and logged" : "Output rejected and logged");
      if (onAttested) onAttested(accepted);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not log attestation");
    } finally { setAttesting(false); }
  };

  return (
    <div className="border border-brand rounded-md bg-brand-bg/40 p-3 space-y-3" data-testid="compliance-shield">
      <div className="flex items-start gap-2 text-[11px] text-brand-muted">
        <ShieldCheck className="w-4 h-4 text-brand-primary shrink-0 mt-0.5" />
        <div className="leading-relaxed">
          <b className="text-brand-text">AI-Assisted Clinical Decision Support.</b> Physician review required.
          ZidaMD does not provide diagnosis — final clinical judgement is yours.
        </div>
      </div>
      <ConfidenceBadge confidence={confidence} sources={sources} modelRef="Claude Sonnet 4.5" compact testid={`shield-conf-${outputId}`} />
      {!attested ? (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => attest(true)}
            disabled={attesting}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-brand-primary text-white text-xs font-semibold hover:bg-[#35096B] disabled:opacity-50"
            data-testid={`shield-attest-accept-${outputId}`}
          >
            <CheckCircle2 className="w-4 h-4" />
            {attesting ? "Logging…" : "I've reviewed — accept responsibility"}
          </button>
          <button
            type="button"
            onClick={() => attest(false)}
            disabled={attesting}
            className="px-3 py-2 rounded-md border border-brand text-xs text-brand-muted hover:text-brand-text"
            data-testid={`shield-attest-reject-${outputId}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-[11px] text-emerald-700" data-testid={`shield-attested-${outputId}`}>
          <CheckCircle2 className="w-4 h-4" />
          <span>Attestation logged to audit trail.</span>
        </div>
      )}
    </div>
  );
}
